import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import { QAAssignmentParams } from '@scale/shared/types/qaOperation/qaOperation';
import SelectWorkerTeam from 'react-scale/corp/components/UI/SelectWorkerTeam';
import { Chip } from 'design-system';
import _ from 'lodash';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { RemoAdminPersonaDataScope } from '@scale/shared/types/remoAdmin';

const MAX_ASSIGNEES = 10;

interface QAOperationAssignmentFormProps {
  onChange: (assignmentParams: QAAssignmentParams | undefined) => void;
  isCreateQAOperationFormField?: boolean;
}

export const QAOperationAssignmentForm = React.memo<QAOperationAssignmentFormProps>(
  function QAOperationAssignmentForm({ onChange, isCreateQAOperationFormField }) {
    const [userValues, setUserValues] = React.useState<SelectValue[]>([]);
    const [workerTeamValues, setWorkerTeamValues] = React.useState<SelectValue[]>([]);
    const [userValue, setUserValue] = React.useState<SelectValue | null>(null);
    const [teamValue, setTeamValue] = React.useState<SelectValue | null>(null);
    const { isRemoAdmin, currentPersona } = useRemoAdminCtx();

    React.useEffect(() => {
      setUserValues(prev =>
        userValue?.value ? _.uniq([...prev, userValue]).slice(0, MAX_ASSIGNEES) : prev,
      );
    }, [userValue]);

    React.useEffect(() => {
      setWorkerTeamValues(prev =>
        teamValue?.value ? _.uniq([...prev, teamValue]).slice(0, MAX_ASSIGNEES) : prev,
      );
    }, [teamValue]);

    React.useEffect(() => {
      if (!userValues.length && !workerTeamValues.length) {
        onChange(undefined);
        return;
      }

      const userIds = userValues.map(row => row.value);
      const workerTeamIds = workerTeamValues.map(row => row.value);
      const assignmentParams: QAAssignmentParams = {
        userIds,
        workerTeamIds,
      };
      onChange(assignmentParams);
    }, [userValues, workerTeamValues]);

    const handleDeleteUser = React.useCallback((user: SelectValue) => {
      setUserValues(prev => prev.filter(row => row.value !== user.value));
    }, []);

    const handleDeleteTeam = React.useCallback((team: SelectValue) => {
      setWorkerTeamValues(prev => prev.filter(row => row.value !== team.value));
    }, []);

    // User can assign if they're in Corp, or if they're a RemoAdmin QM in their current persona
    if (isRemoAdmin && currentPersona?.dataScope !== RemoAdminPersonaDataScope.QM) return null;

    return (
      <Stack spacing={1}>
        {!isCreateQAOperationFormField && (
          <Typography variant="subtitle1">Assign to Other Users (Optional)</Typography>
        )}
        <Typography variant="subtitle2">Assign Individual Users</Typography>
        <Paper sx={{ padding: 1 }}>
          <Stack spacing={1} direction="row" flexWrap="wrap" rowGap={1}>
            {userValues.map(user => (
              <Chip key={user.value} label={user.label} onDelete={() => handleDeleteUser(user)} />
            ))}
            {userValues.length === 0 && <Typography variant="body2">No users assigned.</Typography>}
          </Stack>
        </Paper>
        <SelectScaler value={userValue} onChange={setUserValue} placeholder="User" />

        <Typography variant="subtitle2">Assign Worker Teams</Typography>
        <Paper sx={{ padding: 1 }}>
          <Stack spacing={1} direction="row" flexWrap="wrap" rowGap={1}>
            {workerTeamValues.map(team => (
              <Chip key={team.value} label={team.label} onDelete={() => handleDeleteTeam(team)} />
            ))}
            {workerTeamValues.length === 0 && (
              <Typography variant="body2">No worker teams assigned.</Typography>
            )}
          </Stack>
        </Paper>
        <SelectWorkerTeam value={teamValue} onChange={setTeamValue} placeholder="Worker Team" />
      </Stack>
    );
  },
);

interface SelectValue {
  label: string;
  value: string;
}
